import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Coerce empty / whitespace-only strings to undefined so `.optional()` accepts
 * them. Mirrors the backend `remoteDesktopCredentialsConfigSchema`
 * (backend/src/core/ai/tools/remote-desktop/config.ts) — kept in sync by hand
 * because `packages/*` cannot import from `backend`.
 */
const optionalTrimmedUuid = z.preprocess((value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}, z.string().uuid().optional());

const remoteDesktopStepCredentialsSchema = z.object({
  avdUrl: z
    .string()
    .url()
    .default("https://windows.cloud.microsoft.com/#/devices"),
  email: z.string().trim().default(""),
  password: z.string().default(""),
  otpTargetId: z.string().optional(),
  vmPassword: z.string().default(""),
  desktopName: z.string().min(1).default("AVD-FullDesktop"),
  authStateName: z.string().optional(),
  headless: z.boolean().default(false),
  desktopLoadWaitSeconds: z.number().int().positive().max(120).default(10),
  credentialId: optionalTrimmedUuid,
  vmCredentialId: optionalTrimmedUuid,
});

export const loginToRemoteDesktopStep = defineStepConfig({
  type: "login_to_remote_desktop",
  name: "Login to Remote Desktop",
  description:
    "Create and connect a remote desktop session using the credentials configured on this step. Returns a session ID for downstream remote-desktop steps.",
  category: "Computer Use",

  inputSchema: z.object({}),

  outputSchema: z.object({
    sessionId: z.string().nullable(),
    message: z.string().nullable(),
    error: z.string().nullable(),
    screenshotDocumentId: z.string().nullable(),
    screenshotStoragePath: z.string().nullable(),
  }),

  configSchema: z.object({
    credentials: remoteDesktopStepCredentialsSchema.optional(),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
